<script setup lang="ts">
import { computed } from 'vue';

import { Badge } from '@/components/ui/Badge';
import { CustomCollapsible } from '@/components/custom/CustomCollapsible';
import { CustomAlert } from '@/components/custom/CustomAlert';

import { COMPONENT_VARIANTS } from '@/constants/component';

import { ContainerNamespace } from '@/types/containers';
import type { UserSimplifiedWithContainerOwnership } from '@/types/users';

const props = defineProps<{
  simplifiedUsersWithContainerOwnership: UserSimplifiedWithContainerOwnership[];
  namespace?: ContainerNamespace;
}>();

const containerOwners = computed(() => {
  return props.simplifiedUsersWithContainerOwnership.filter(user => {
    if (!props.namespace) {
      return user.isPortfolioOwner || user.isMonitoringOwner;
    }

    return props.namespace === ContainerNamespace.NAMESPACE_MONITORING
      ? user.isMonitoringOwner
      : user.isPortfolioOwner;
  });
});
</script>

<template>
  <CustomAlert
    v-if="containerOwners.length > 0"
    :variant="COMPONENT_VARIANTS.warning"
    :heading="`${$t('common.notice')}!`"
    class="items-center"
    test-id="container-owners-alert-affected-users"
  >
    <p class="font-normal">
      {{ $t('manageAccounts.delete_users_containers_warning') }}
    </p>

    <CustomCollapsible
      class="bg-amber-100 border-yellow-200 text-yellow-800 justify-start"
      trigger-styles="bg-yellow-100 text-yellow-900 justify-start"
      content-class=" "
      test-id="container-owners-alert-affected-users-trigger"
      :trigger-label="$t('manageAccounts.affected_users')"
    >
      <template #badge>
        <Badge test-id="container-owners-alert-affected-users-badge" :variant="COMPONENT_VARIANTS.warning" is-counter>
          <span>{{ containerOwners.length }}</span>
        </Badge>
      </template>

      <div
        class="flex w-full bg-amber-100 text-yellow-900 justify-start p-2 border border-yellow-300 rounded-lg outline outline-amber-100"
      >
        <div class="max-h-36 overflow-y-auto">
          <span data-testid="container-owners-alert-affected-users-list">
            {{ containerOwners.map(user => user.fullName).join(', ') }}
          </span>
        </div>
      </div>
    </CustomCollapsible>
  </CustomAlert>
</template>
